import type { Projection, SettlementRow } from '@/api';
import { InfoTooltip, KpiCard, KpiSkeleton } from '@/components';
import { CurrencyValue, DataTable, Flex, Heading } from '@blockanalitica/ui';
import { TableColumn } from '@blockanalitica/ui/package/types/src/components/molecules/DataTable';

interface AnnualizedProjectionProps {
  data?: Projection | null;
}

const currencyOptions = { currency: 'USD', notation: 'compact' as const };

function currencyCell(value: number | null) {
  return <CurrencyValue value={value} options={currencyOptions} />;
}

export default function AnnualizedProjection({
  data
}: AnnualizedProjectionProps) {
  if (!data) {
    return (
      <Flex gap={['m', 's']} flexDirection={['column', 'row']}>
        <KpiSkeleton />
        <KpiSkeleton />
        <KpiSkeleton />
      </Flex>
    );
  }

  const totals = {
    income: data.income,
    expenses: data.expenses,
    net_revenue: data.net_revenue
  };

  const columns: TableColumn<SettlementRow>[] = [
    { header: 'MSC', getCell: (row) => row.name },
    { header: 'Period', getCell: (row) => row.period },
    {
      header: 'Gross Revenue',
      cellAlign: 'flex-end',
      headerAlign: 'flex-end',
      getCell: (row) => currencyCell(row.income)
    },
    {
      header: 'Expenses',
      cellAlign: 'flex-end',
      headerAlign: 'flex-end',
      getCell: (row) => currencyCell(row.expenses)
    },
    {
      header: 'Net Revenue',
      cellAlign: 'flex-end',
      headerAlign: 'flex-end',
      getCell: (row) => currencyCell(row.net_revenue)
    }
    // {
    //   header: 'Net Surplus',
    //   cellAlign: 'flex-end',
    //   headerAlign: 'flex-end',
    //   getCell: (row) => currencyCell(row.net_profit)
    // }
  ];

  return (
    <Flex flexDirection="column" gap="s">
      <Flex gap="3xs" alignItems="center">
        <Heading size="4">Annualized Projection</Heading>
        <InfoTooltip text="Average of the last 3 settlements (~3 months of activity), scaled to a full year. Indicative only — actuals will diverge with rates, allocations, and one-off items." />
      </Flex>
      <Flex gap={['m', 's']} flexDirection={['column', 'row']}>
        <KpiCard
          title="Projected Gross Revenue"
          field="income"
          data={totals}
          options={currencyOptions}
        />
        <KpiCard
          title="Projected Net Revenue"
          field="net_revenue"
          data={totals}
          options={currencyOptions}
        />
        <KpiCard
          title="Projected Expenses"
          field="expenses"
          data={totals}
          options={currencyOptions}
        />
      </Flex>
      <DataTable
        rows={data.basis}
        columns={columns}
        getKey={(row) => row.slug}
      />
    </Flex>
  );
}
